import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "../lib/providers";
import { Btn } from "./primitives";
import { toast } from "sonner";
import { UploadSimple, FileText, X } from "@phosphor-icons/react";

export default function QuoteCsvImportModal({ open, onClose, onLinesImported, lang }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const de = lang === "de";

  if (!open) return null;

  const close = () => { setResult(null); onClose(); };

  const downloadTemplate = async () => {
    setBusy(true);
    try {
      const res = await api.get("/positions/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "positionen-vorlage.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/positions/import.csv?with_lines=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.lines?.length) onLinesImported(data.lines);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={close}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-sm w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="quote-csv-import-modal">
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-start justify-between">
          <div>
            <div className="label-overline">{de ? "CSV-Import" : "CSV import"}</div>
            <h3 className="font-heading text-lg font-semibold">
              {result ? (de ? "Import abgeschlossen" : "Import done") : (de ? "Positionen per CSV importieren" : "Import positions via CSV")}
            </h3>
          </div>
          <button type="button" onClick={close} className="text-[var(--muted-2)] hover:text-[var(--ink)]" data-testid="quote-csv-import-close-x"><X size={18} /></button>
        </div>

        {!result ? (
          <div className="px-6 py-5 space-y-4 text-sm overflow-y-auto">
            <p className="text-[var(--muted)]">
              {de
                ? "Artikel aus einer CSV-Datei werden in den Katalog aufgenommen (gleiche Regeln wie der Katalog-Import) und gleichzeitig als Positionen in dieses Angebot eingefügt."
                : "Items from a CSV file are added to the catalog (same rules as the catalog import) and inserted as line items into this quote at the same time."}
            </p>
            <ul className="list-disc pl-5 space-y-1 text-[var(--muted)] text-xs">
              <li>{de ? "Format: number; name; description; category_number; category_name; unit; unit_price; purchase_price; tiers_json" : "Format: number; name; description; category_number; category_name; unit; unit_price; purchase_price; tiers_json"}</li>
              <li>{de ? "Optionale Spalte quantity: Menge pro Angebots-Position (Standard: 1, Staffelpreise werden berücksichtigt)" : "Optional quantity column: quantity per quote line (default: 1, tier prices are applied)"}</li>
              <li>{de ? "Duplikate werden im Katalog übersprungen, aber trotzdem ins Angebot übernommen" : "Duplicates are skipped in the catalog but still added to the quote"}</li>
            </ul>
            <div className="flex gap-2 pt-1">
              <Btn variant="ghost" disabled={busy} onClick={downloadTemplate} data-testid="quote-csv-template" className="h-9 px-3">
                <FileText size={14} /> {de ? "Vorlage herunterladen" : "Download template"}
              </Btn>
              <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="quote-csv-import-input" />
              <Btn disabled={busy} onClick={() => inputRef.current?.click()} data-testid="quote-csv-import-upload" className="h-9 px-3">
                <UploadSimple size={14} /> {busy ? "…" : (de ? "CSV-Datei wählen" : "Choose CSV file")}
              </Btn>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4 text-sm overflow-y-auto">
            <div className="p-3 border border-[var(--brand)]/40 bg-[var(--brand)]/5 rounded-sm text-center" data-testid="import-lines-count">
              <span className="text-2xl font-semibold text-[var(--brand)]">{result.lines?.length || 0}</span>{" "}
              <span className="text-[var(--muted)]">{de ? "Positionen ins Angebot übernommen" : "line items added to the quote"}</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 border border-[var(--border)] rounded-sm">
                <div className="text-2xl font-semibold text-[var(--brand)]" data-testid="import-created-count">{result.created || 0}</div>
                <div className="text-xs text-[var(--muted-2)]">{de ? "neu im Katalog" : "created in catalog"}</div>
              </div>
              <div className="text-center p-3 border border-[var(--border)] rounded-sm">
                <div className="text-2xl font-semibold" data-testid="import-updated-count">{result.updated || 0}</div>
                <div className="text-xs text-[var(--muted-2)]">{de ? "aktualisiert" : "updated"}</div>
              </div>
              <div className="text-center p-3 border border-[var(--border)] rounded-sm">
                <div className="text-2xl font-semibold text-[#F59E0B]" data-testid="import-skipped-count">{result.skipped || 0}</div>
                <div className="text-xs text-[var(--muted-2)]">{de ? "übersprungen (Duplikat)" : "skipped (duplicate)"}</div>
              </div>
            </div>
            {result.items_skipped?.length > 0 && (
              <details className="border border-[#F59E0B]/40 rounded-sm bg-[#F59E0B]/5" open data-testid="import-section-skipped">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium hover:bg-[#F59E0B]/10">
                  {de ? `Im Katalog übersprungen — bereits vorhanden (${result.items_skipped.length})` : `Skipped in catalog — already existing (${result.items_skipped.length})`}
                </summary>
                <ul className="px-3 py-2 max-h-40 overflow-y-auto text-xs space-y-0.5">
                  {result.items_skipped.map((it, i) => (
                    <li key={i} className="text-[var(--muted)]">• {it.number || "—"} — {it.name}</li>
                  ))}
                </ul>
              </details>
            )}
            {result.errors?.length > 0 && (
              <div className="text-xs text-[#F59E0B]">
                <div className="font-medium mb-1">{de ? "Hinweise:" : "Notes:"}</div>
                <ul className="space-y-0.5">
                  {result.errors.slice(0, 10).map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              </div>
            )}
            <p className="text-xs text-[var(--muted-2)]">
              {de ? "Die Positionen wurden dem Angebot hinzugefügt — Mengen und Preise kannst du jetzt direkt in der Tabelle anpassen." : "Line items were added to the quote — adjust quantities and prices directly in the table."}
            </p>
          </div>
        )}

        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end">
          <Btn variant={result ? "primary" : "ghost"} onClick={close} data-testid="quote-csv-import-close">{de ? "Schließen" : "Close"}</Btn>
        </div>
      </div>
    </div>,
    document.body
  );
}
